import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Radio } from 'antd';

import FormsTable from '../FormsTable';
import FormDetailView from '../CompletedForms/FormDetailView';
import FormResponder from '../FormResponder';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

import {
  getAssignedForms,
  getAssignedToOthersForms,
  getFormById,
} from '../state/forms.actions';

import {
  getCustomers,
  getProjectIdMap,
} from '../../state/selectors';
import {
  getFilters,
  handleFormClick,
  parseAssignedForm,
  getAssignedToMyselfForms,
} from '../formHelpers';
import Permissions from '../../auth/Permissions';
import { getFormsHomeAssignedColumns } from '../FormColumns';
import {
  ASSIGNED_TO_MYSELF_TAB,
  ASSIGNED_TO_OTHERS_TAB,
  FORMS_HOME_PREVIEW_LIMIT,
  getAssignedNotStartedCount,
} from './formsHomeHelpers';

export default function FormsHomeAssignedSection({ onCardColumnClick, history }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const assignedForms = useSelector((state) => state.forms.assignedForms);
  const {
    manual: manualDrafts = {},
    assigned: assignedDrafts = {},
  } = useSelector((state) => state.forms.drafts);
  const customers = useSelector(getCustomers);
  const projectIdMap = useSelector(getProjectIdMap);
  const templates = useSelector((state) => state.forms.templates);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const assignedToOthersForms = useSelector((state) => state.forms.assignedToOthersForms);

  const [tab, setTab] = useState(ASSIGNED_TO_MYSELF_TAB);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [assignedForm, setAssignedForm] = useState({});
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);
  const [showFormDetail, setShowFormDetail] = useState(false);

  const hasAssignedToOthersPermission = Permissions.has('FORMS_WRITE');

  useEffect(() => {
    if (tab === ASSIGNED_TO_OTHERS_TAB) {
      dispatch(getAssignedToOthersForms());
    }
  }, [tab]);

  useEffect(() => {
    if (tab === ASSIGNED_TO_MYSELF_TAB) {
      setLoading(true);
      dispatch(getAssignedForms()).finally(() => setLoading(false));
    }
  }, [tab]);

  const assignedToMeForms = useMemo(() => (
    getAssignedToMyselfForms({
      assignedForms,
      assignedDrafts,
      manualDrafts,
      templates,
      projectIdMap,
      customerIdMap: customers,
    })
  ), [assignedForms, assignedDrafts, manualDrafts, templates, projectIdMap, customers]);
  const assignedToOthersFormsParsed = useMemo(() => (
    assignedToOthersForms.map((form) => parseAssignedForm({
      form,
      type: 'Assigned',
      assignedDrafts,
      templates,
      projectIdMap,
      customerIdMap: customers,
    }))
  ), [assignedToOthersForms, assignedDrafts, templates, projectIdMap, customers]);

  const tableData = tab === ASSIGNED_TO_MYSELF_TAB
    ? assignedToMeForms
    : assignedToOthersFormsParsed;

  const previewData = useMemo(
    () => tableData.slice(0, FORMS_HOME_PREVIEW_LIMIT),
    [tableData],
  );

  const notStartedCount = useMemo(
    () => getAssignedNotStartedCount({ assignedForms, assignedDrafts }),
    [assignedForms, assignedDrafts],
  );

  const assignedCustomerFilter = useMemo(
    () => getFilters({ data: previewData, idMap: customers, key: 'customers' }),
    [previewData, customers],
  );
  const assignedProjectFilter = useMemo(
    () => getFilters({
      data: previewData,
      idMap: projectIdMap,
      key: 'projects',
    }),
    [previewData, projectIdMap],
  );
  const cardFilters = useMemo(() => {
    const cardSet = new Set(previewData.map(({ card }) => card).filter(Boolean));
    return Array.from(cardSet).map((card) => ({ text: card, value: card }));
  }, [previewData]);

  const columns = useMemo(() => getFormsHomeAssignedColumns({
    projectFilters: assignedProjectFilter,
    customerFilters: assignedCustomerFilter,
    cardFilters,
    projectIdMap,
    settings,
    onCardColumnClick,
    t,
  }), [
    assignedProjectFilter,
    assignedCustomerFilter,
    cardFilters,
    projectIdMap,
    settings,
    onCardColumnClick,
    t,
  ]);

  const onFormClick = useCallback(async (record) => {
    if (tab === ASSIGNED_TO_OTHERS_TAB) {
      dispatch(getFormById(record.id, { getRecentDraft: true }));
      setShowFormDetail(true);
      return;
    }
    await handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setAssignedForm,
      setShowDetail,
      setSelectedIsEdit,
      setSelectedIsResubmit,
    });
  }, [dispatch, loading, tab]);

  return (
    <section className="forms-home-section">
      <div className="forms-home-section-header">
        <div>
          <h2 className="forms-home-section-title">Assigned</h2>
          <p className="forms-home-section-subtitle">
            {notStartedCount} not started — rows with a Draft tag have been opened and saved without submitting
          </p>
        </div>
        <div className="forms-home-section-actions">
          {hasAssignedToOthersPermission && (
            <OnTraccrRadioGroup
              value={tab}
              size="medium"
              buttonStyle="outline"
              className="forms-home-tab-toggle"
              onChange={(newTab) => setTab(newTab)}
            >
              <Radio.Button value={ASSIGNED_TO_MYSELF_TAB}>
                Assigned to me
              </Radio.Button>
              <Radio.Button value={ASSIGNED_TO_OTHERS_TAB}>
                Assigned to others
              </Radio.Button>
            </OnTraccrRadioGroup>
          )}
          <span
            className="forms-home-view-all"
            onClick={() => history.push('/forms/assigned')}
          >
            View all →
          </span>
        </div>
      </div>
      <FormsTable
        data={previewData}
        columns={columns}
        onClickRow={onFormClick}
        loading={loading}
        isHomePreview
      />
      <FormResponder
        visible={showDetail}
        onClose={() => setShowDetail(false)}
        assignedForm={assignedForm}
        isEdit={selectedIsEdit}
        isResubmit={selectedIsResubmit}
      />
      <FormDetailView
        visible={showFormDetail}
        onClose={() => setShowFormDetail(false)}
        onCardColumnClick={onCardColumnClick}
        zIndex={999}
      />
    </section>
  );
}

FormsHomeAssignedSection.propTypes = {
  onCardColumnClick: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};
